import type { ThreadMessage } from '@assistant-ui/react';
import { z } from 'zod';

import type { Recipe } from '#core/lib/expr/session/bindingResolver.ts';

import { expressionSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import { isToolCallPart } from '#plugin-chat/lib/tool/output-types.ts';
import { isFailurePart } from '#plugin-chat/lib/tool/result-envelope.ts';

/**
 * Conversation dataset bindings are a **pure projection of the active
 * message branch**, not a separately-persisted log and not a mutable
 * store. The producing expression already lives verbatim on every
 * producer tool-call part (`{ outputDataset, expression }`); success /
 * failure is classified through the same `isFailurePart` /
 * `parseResultEnvelope` SSOT everywhere else. The recipes this module
 * extracts — in message order — *are* the binding namespace for whatever
 * branch the user is looking at: a resolver re-evaluates them lazily on
 * demand ({@link createBindingResolver}). Regenerate / edit /
 * branch-switch / reload therefore stay consistent with the transcript by
 * construction, with no second source of truth and nothing to hydrate up
 * front.
 */

/**
 * The producer tools that contribute a dataset binding. Kept in sync
 * with the two `<Tool>` producers — `QueryKnowledgeGraphTool`
 * (`withMention('queryKnowledgeGraph', …)`) and `TransformDatasetTool`
 * (`withMention('transformDataset', …)`). A non-producer tool never
 * carries `{ outputDataset, expression }`, so the schema guard below is
 * the real gate; this set is the cheap pre-filter.
 */
const PRODUCER_TOOL_NAMES: ReadonlySet<string> = new Set(['queryKnowledgeGraph', 'transformDataset']);

/**
 * Both producer input schemas (`QueryKnowledgeGraphInputSchema`,
 * `TransformDatasetInputSchema`) carry these two fields verbatim — they
 * are the only keys with binding semantics. Extra keys on the persisted
 * args (e.g. older shapes with `inputDataset`) are tolerated and
 * ignored. Validated here because persisted parts are a deserialization
 * boundary (older/corrupt shapes must not throw — they're skipped,
 * mirroring the rest of the localStorage layer).
 */
const ProducerArgsSchema = z.object({
  outputDataset: z.string().min(1),
  expression: expressionSchema,
});

/**
 * Extract the ordered binding recipes from a flat list of message
 * parts. A part contributes a recipe iff it is a producer tool-call
 * whose result is a **success** envelope and whose args parse to
 * `{ outputDataset, expression }`. The unevaluated `expression` string
 * is carried as-is — the resolver re-derives the value by evaluating it
 * against the prefix before it; nothing is pre-parsed or pre-evaluated
 * here.
 *
 * In-progress calls (no result yet), protocol-error calls (`isError`),
 * and structured failures are skipped — a failed/streaming producer
 * never contributes a binding. Works on both the persisted
 * `ThreadMessage.content` shape and the live assistant-ui
 * `MessageState.parts` shape (the predicate only touches the
 * overlapping `type`/`toolName`/`args`/`result`/`isError` fields), so
 * the render-time (`useBindingScope`) and execute-time
 * (`useThreadScopedEval`) paths share one projector.
 */
export const recipesFromParts = (parts: readonly unknown[]): Recipe[] =>
  parts.flatMap((part): Recipe[] => {
    if (!isToolCallPart(part) || !PRODUCER_TOOL_NAMES.has(part.toolName)) {
      return [];
    }
    if (part.result === undefined || isFailurePart(part)) {
      return [];
    }
    const args = ProducerArgsSchema.safeParse(part.args);
    if (!args.success) {
      return [];
    }
    return [{ name: args.data.outputDataset, expression: args.data.expression }];
  });

/**
 * Extract the ordered binding recipes for an active message branch.
 * Order is message order × part order — the same order the bindings
 * are produced in, so a later `(col <earlier> …)` reference
 * resolves against the strict prefix before it. `ThreadMessage` exposes
 * its parts under `.content`.
 */
export const recipesFromMessages = (messages: readonly ThreadMessage[]): Recipe[] =>
  messages.flatMap((message) => recipesFromParts(message.content ?? []));
